import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import AppLayout from '../../components/ui/AppLayout';
import HeaderBar from '../../components/ui/HeaderBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { userAPI, recruiterAPI } from '../../utils/api';
import * as XLSX from 'xlsx';
import { getRoleKey } from '../../utils/authHelpers';
import { useNotifier } from '../../context/NotificationContext';

// Components
import UserTable from './components/UserTable';
import AddUserModal from './components/AddUserModal';
import EditUserModal from './components/EditUserModal';
import StatusChangeModal from './components/StatusChangeModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import BulkActionsModal from './components/BulkActionsModal';
import PaginationComponent from '../../components/PaginationComponent';

const UserManagement = () => {
  const { user } = useAuth();
  const notify = useNotifier();
  const { 
    users: allUsers, 
    recruiters, 
    fetchUsers, 
    fetchRecruiters, 
    refreshUsers,
    loading: dataLoading 
  } = useData();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  // Data States
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const breadcrumbItems = [
    { label: 'Master Admin Dashboard', path: '/master-admin-dashboard' },
    { label: 'User Management', path: '/user-management' }
  ];

  // User role options (excluding employer)
  const userRoles = [
    { value: 'admin', label: 'Master Admin', description: 'Full system access and management' },
    { value: 'clab', label: 'CLAB User', description: 'Biodata review and approval' },
    { value: 'recruiter', label: 'Recruiter', description: 'Biodata submission and management' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchRecruiters();
  }, [fetchUsers, fetchRecruiters]);

  const users = useMemo(() => {
    if (!allUsers) return [];
    return allUsers.filter(u => getRoleKey(u.role) !== 'employer');
  }, [allUsers]);

  // Stats calculation
  const stats = useMemo(() => {
    return {
      total: users.length,
      active: users.filter(u => (u.status || '').toLowerCase() === 'active' || u.status === 0).length,
      recruiters: users.filter(u => getRoleKey(u.role) === 'recruiter').length,
      clab: users.filter(u => getRoleKey(u.role) === 'clab').length,
    };
  }, [users]);

  // Utility to get role display name
  const getRoleDisplayName = (role) => {
    const roleKey = getRoleKey(role);
    const roleInfo = userRoles.find(r => r.value === roleKey);
    return roleInfo ? roleInfo.label : 'Unknown';
  };

  // Filter and search users
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    
    return users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (user.full_name || '').toLowerCase().includes(searchLower) ||
        (user.username || '').toLowerCase().includes(searchLower) ||
        (user.email || '').toLowerCase().includes(searchLower);
      
      const userRoleKey = getRoleKey(user.role);
      const matchesRole = !roleFilter || userRoleKey === roleFilter;

      const userStatus = (user.status || '').toString().toLowerCase();
      const matchesStatus = !statusFilter || userStatus === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Pagination logic
  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const paginationData = {
    currentPage,
    perPage: itemsPerPage,
    total: totalUsers,
    totalPages,
    from: totalUsers > 0 ? startIndex + 1 : 0,
    to: Math.min(endIndex, totalUsers)
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedUsers([]); // Clear selection when changing pages
  };

  const handlePerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page
    setSelectedUsers([]); // Clear selection
  };

  // User Actions
  const handleAddUser = () => {
    setShowAddModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleChangeStatus = (user) => {
    setSelectedUser(user);
    setShowStatusModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleBulkActions = () => {
    setShowBulkModal(true);
  };

  // Callback for when a user is successfully created
  const onUserAdded = () => {
    refreshUsers();
    setShowAddModal(false);
  }

  // Update User
  const handleUpdateUser = async (isSuccess, message) => {
    if (isSuccess) {
      refreshUsers();
      setShowEditModal(false);
      setSelectedUser(null);
      showNotification('success', message || 'User updated successfully!');
    } else {
      showNotification('error', message || 'Failed to update user.');
      setError(message || 'Failed to update user.');
    }
  };

  // Update User Status
  const handleUpdateStatus = async (statusData) => {
    try {
      // setLoading(true); // This line was removed from the new_code, so it's removed here.
      
      const result = await userAPI.updateUserStatus(selectedUser.id, statusData);

      if (result.data) {
        refreshUsers();
        setShowStatusModal(false);
        setSelectedUser(null);
        showNotification('success', 'User status updated successfully!');
      } else {
        showNotification('error', result.message || 'Failed to update status');
        setError(result.message || 'Failed to update status');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while updating status.';
      console.error('Failed to update status:', error);
      showNotification('error', errorMessage);
      setError(errorMessage);
    } finally {
      // setLoading(false); // This line was removed from the new_code, so it's removed here.
    }
  };

  // Delete User
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    try {
      await userAPI.deleteUser(selectedUser.id);
      refreshUsers();
      setShowDeleteModal(false);
      setSelectedUser(null);
      showNotification('success', 'User deleted successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete user.';
      console.error('Failed to delete user:', error);
      showNotification('error', errorMessage);
      setError(errorMessage);
    }
  };

  // Bulk Actions
  const handleBulkAction = async (action, actionData = {}) => {
    try {
      // setLoading(true); // This line was removed from the new_code, so it's removed here.
      
      if (action === 'delete') {
        const results = await Promise.all(
          selectedUsers.map(userId => userAPI.deleteUser(userId))
        );
        
        const successful = results.filter(r => r.success).length;
        showNotification('success', `${successful} users deleted successfully!`);
      } 
      else if (action === 'status_change') {
        const results = await Promise.all(
          selectedUsers.map(userId => 
            userAPI.updateUserStatus(userId, {
              status: actionData.status,
              remarks: actionData.remarks,
              updated_by: user.id
            })
          )
        );
        
        const successful = results.filter(r => r.success).length;
        showNotification('success', `${successful} users status updated successfully!`);
      }

      refreshUsers(); // Changed from loadInitialData()
      setSelectedUsers([]);
      setShowBulkModal(false);
      
    } catch (error) {
      console.error('Bulk action failed:', error);
      setError('Bulk action failed. Please try again.');
    } finally {
      // setLoading(false); // This line was removed from the new_code, so it's removed here.
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    try {
      const exportData = filteredUsers.map(user => {
        const recruiter = recruiters.find(r => r.id === user.recruiter_id);
        return {
          'User ID': user.id,
          'Full Name': user.name,
          'Email': user.email,
          'Role': userRoles.find(r => r.value === user.role)?.label || user.role,
          'Status': user.status,
          'Recruiter Country': recruiter?.country || '',
          'Last Login': user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never',
          'Created Date': new Date(user.created_at).toLocaleString(),
          'Remarks': user.remarks || '',
          'Created By': user.created_by_name || '',
          'Updated By': user.updated_by_name || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = colWidths;

      const fileName = `users_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showNotification('success', `Exported ${exportData.length} users to ${fileName}`);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Export failed. Please try again.');
    }
  };

  // Selection handlers
  const handleUserSelect = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      // Select all users on current page
      const currentPageUserIds = currentUsers.map(user => user.id);
      setSelectedUsers(prev => [...new Set([...prev, ...currentPageUserIds])]);
    } else {
      // Deselect all users on current page
      const currentPageUserIds = currentUsers.map(user => user.id);
      setSelectedUsers(prev => prev.filter(id => !currentPageUserIds.includes(id)));
    }
  };

  const showNotification = (type, message) => notify.show(type, message);

  return (
    <AppLayout sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
      <Helmet>
        <title>User Management | WorkerLink</title>
      </Helmet>

      <div className="flex-1 flex flex-col min-h-screen bg-secondary-50">
        <HeaderBar 
          userRole={user?.role || 'admin'}
          userName={user?.name || user?.full_name || 'Master Admin'}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />
        
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNavigation items={breadcrumbItems} />
            
            {/* Header */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">User Management</h1>
                  <p className="text-secondary-600 mt-1">
                    Manage system users, roles, and permissions (excludes employers)
                  </p>
                </div>
                <div className="mt-4 sm:mt-0 flex space-x-3">
                  <button
                    onClick={handleExportExcel}
                    disabled={filteredUsers.length === 0}
                    className="inline-flex items-center px-4 py-2 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Export Excel
                  </button>
                  <button
                    onClick={handleAddUser}
                    className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <Icon name="UserPlus" size={16} className="mr-2" />
                    Add User
                  </button>
                </div>
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-error mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-error-800">Error</h3>
                    <p className="text-sm text-error-700 mt-1">{error}</p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="ml-auto text-error-400 hover:text-error-600"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-secondary-200 p-4">
                <div className="text-2xl font-bold text-primary">
                  {stats.total}
                </div>
                <div className="text-sm text-secondary-600">Total Users</div>
              </div>
              <div className="bg-white rounded-lg border border-secondary-200 p-4">
                <div className="text-2xl font-bold text-success">
                  {stats.active}
                </div>
                <div className="text-sm text-secondary-600">Active Users</div>
              </div>
              <div className="bg-white rounded-lg border border-secondary-200 p-4">
                <div className="text-2xl font-bold text-accent">
                  {stats.recruiters}
                </div>
                <div className="text-sm text-secondary-600">Recruiters</div>
              </div>
              <div className="bg-white rounded-lg border border-secondary-200 p-4">
                <div className="text-2xl font-bold text-warning">
                  {stats.clab}
                </div>
                <div className="text-sm text-secondary-600">CLAB Users</div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border border-secondary-200 p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Search Users
                  </label>
                  <div className="relative">
                    <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Role
                  </label>
                                     <select
                     className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                     value={roleFilter}
                     onChange={(e) => setRoleFilter(e.target.value)}
                   >
                     <option value="">All Roles</option>
                     {userRoles.map(role => (
                       <option key={role.value} value={role.value}>{role.label}</option>
                     ))}
                   </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Status
                  </label>
                                     <select
                     className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                     value={statusFilter}
                     onChange={(e) => setStatusFilter(e.target.value)}
                   >
                     <option value="">All Status</option>
                     <option value="active">Active</option>
                     <option value="inactive">Inactive</option>
                   </select>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <UserTable
              users={currentUsers}
              recruiters={recruiters}
              selectedUsers={selectedUsers}
              onUserSelect={handleUserSelect}
              onSelectAll={handleSelectAll}
              onEditUser={handleEditUser}
              onChangeStatus={handleChangeStatus}
              onDeleteUser={handleDeleteUser}
              loading={dataLoading?.users || false}
              getRoleDisplayName={getRoleDisplayName}
            />

            {/* Pagination */}
            {totalUsers > 0 && (
              <div className="mt-6">
                <PaginationComponent
                  pagination={paginationData}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  showPerPageSelector={true}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {/* Debug: Recruiters at render time */}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={onUserAdded}
        recruiters={recruiters}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={selectedUser}
        onUserUpdated={handleUpdateUser}
        showNotification={showNotification}
        recruiters={recruiters}
        userRoles={userRoles}
      />

      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onSave={handleUpdateStatus}
        user={selectedUser}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        user={selectedUser}
      />

      <BulkActionsModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onAction={handleBulkAction}
        selectedCount={selectedUsers.length}
      />
    </AppLayout>
  );
};

export default UserManagement;